import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { Navigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Pencil, Trash2, BookOpen, Dumbbell, SlidersHorizontal, ChevronLeft, ChevronRight, X } from "lucide-react";
import ExerciseFormDialog from "@/components/exercises/ExerciseFormDialog";
import { getBodyTargetImage } from "@/utils/workoutImages";

const BODY_PART_TO_CATEGORY = {
  chest: "chest",
  back: "back",
  shoulders: "shoulders",
  neck: "shoulders",
  "upper arms": "arms",
  "lower arms": "arms",
  "upper legs": "legs",
  "lower legs": "legs",
  waist: "core",
  cardio: "cardio",
};

const CATEGORY_COLORS = {
  chest: "bg-red-500/10 text-red-400 border-red-500/20",
  back: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  shoulders: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  arms: "bg-orange-500/10 text-orange-400 border-orange-500/20",
  legs: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  core: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
  cardio: "bg-pink-500/10 text-pink-400 border-pink-500/20",
  full_body: "bg-teal-500/10 text-teal-400 border-teal-500/20",
};

const DIFFICULTY_COLORS = {
  beginner: "bg-green-500/10 text-green-400 border-green-500/20",
  intermediate: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
  advanced: "bg-red-500/10 text-red-400 border-red-500/20",
};

const CATEGORIES = ["all", "chest", "back", "shoulders", "arms", "legs", "core", "cardio", "full_body"];
const DIFFICULTIES = ["all", "beginner", "intermediate", "advanced"];
const PER_PAGE = 24;

const normalise = (ex) => {
  const bodyParts = ex.bodyParts || (ex.body_part ? [ex.body_part] : []);
  const equipments = ex.equipments || (ex.equipment ? [ex.equipment] : []);
  const targetMuscles = ex.targetMuscles ||
    (ex.muscles_primary ? ex.muscles_primary.split(/,\s*/).filter(Boolean) : []);
  const secondaryMuscles = ex.secondaryMuscles ||
    (ex.muscles_secondary ? ex.muscles_secondary.split(/,\s*/).filter(Boolean) : []);
  const instructions = Array.isArray(ex.instructions) ? ex.instructions : [];
  const media = ex.gifUrl || ex.gif_url || ex.video_url || "";
  const rawCategory = (bodyParts[0] || ex.category || "").toLowerCase();
  const category = BODY_PART_TO_CATEGORY[rawCategory] || "full_body";
  const difficulty = (ex.difficulty || "").toLowerCase();
  return { ...ex, bodyParts, equipments, targetMuscles, secondaryMuscles, instructions, media, category, difficulty };
};

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div className="space-y-1 flex-1 min-w-[140px]">
      <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
        {label}
      </label>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm capitalize"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o === "all" ? "All" : o.replace(/_/g, " ")}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function ExerciseLibrary() {
  const { user } = useAuth();
  const isTrainer = user?.role === "admin" || user?.role === "trainer";
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [equipment, setEquipment] = useState("all");
  const [difficulty, setDifficulty] = useState("all");
  const [targetMuscle, setTargetMuscle] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data: raw = [], isLoading } = useQuery({
    queryKey: ["exercises-library"],
    queryFn: () => base44.entities.Exercises.list("name", 5000),
  });

  const exercises = useMemo(() => raw.map(normalise), [raw]);
  const rawById = useMemo(
    () => Object.fromEntries(raw.map((r) => [r.id, r])),
    [raw],
  );

  // Distinct option lists for the advanced filters, computed from the data.
  const equipmentOptions = useMemo(() => {
    const s = new Set();
    exercises.forEach((ex) => (ex.equipments || []).forEach((e) => s.add(e)));
    return ["all", ...Array.from(s).sort()];
  }, [exercises]);
  const muscleOptions = useMemo(() => {
    const s = new Set();
    exercises.forEach((ex) => (ex.targetMuscles || []).forEach((m) => s.add(m)));
    return ["all", ...Array.from(s).sort()];
  }, [exercises]);

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.Exercises.create(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["exercises-library"] }),
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Exercises.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["exercises-library"] }),
  });
  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.Exercises.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["exercises-library"] }),
  });

  // Wrappers that also reset pagination when the user changes a filter.
  const setSearchAndReset = (v) => { setSearch(v); setPage(1); };
  const setCategoryAndReset = (v) => { setCategory(v); setPage(1); };
  const setEquipmentAndReset = (v) => { setEquipment(v); setPage(1); };
  const setDifficultyAndReset = (v) => { setDifficulty(v); setPage(1); };
  const setTargetMuscleAndReset = (v) => { setTargetMuscle(v); setPage(1); };

  const q = search.toLowerCase().trim();
  const filtered = useMemo(() => {
    return exercises.filter((ex) => {
      const haystack = [
        ex.name,
        ...(ex.bodyParts || []),
        ...(ex.equipments || []),
        ...(ex.targetMuscles || []),
        ...(ex.secondaryMuscles || []),
      ]
        .join(" ")
        .toLowerCase();
      if (q && !haystack.includes(q)) return false;
      if (category !== "all" && ex.category !== category) return false;
      if (difficulty !== "all" && ex.difficulty !== difficulty) return false;
      if (equipment !== "all" && !(ex.equipments || []).includes(equipment)) return false;
      if (targetMuscle !== "all" && !(ex.targetMuscles || []).includes(targetMuscle)) return false;
      return true;
    });
  }, [exercises, q, category, difficulty, equipment, targetMuscle]);

  // Page is auto-clamped via Math.min below — if filters narrow the results
  // we just show the last valid page rather than juggling effect-driven resets.
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageSafe = Math.min(page, totalPages);
  const pageStart = (pageSafe - 1) * PER_PAGE;
  const pageItems = filtered.slice(pageStart, pageStart + PER_PAGE);

  const activeFilterCount = [
    category !== "all",
    difficulty !== "all",
    equipment !== "all",
    targetMuscle !== "all",
  ].filter(Boolean).length;

  const clearFilters = () => {
    setSearch("");
    setCategory("all");
    setDifficulty("all");
    setEquipment("all");
    setTargetMuscle("all");
    setPage(1);
  };

  const handleSubmit = (data) => {
    if (editing) {
      updateMutation.mutate({ id: editing.id, data });
    } else {
      createMutation.mutate(data);
    }
    setEditing(null);
  };

  if (!isTrainer) return <Navigate to="/" replace />;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold tracking-tight">Exercise Library</h1>
          <p className="text-muted-foreground mt-1">
            {exercises.length.toLocaleString()} exercises · search & filter
          </p>
        </div>
        <Button
          data-testid="exercise-add-btn"
          className="gap-2 shrink-0"
          onClick={() => { setEditing(null); setShowForm(true); }}
        >
          <Plus className="w-4 h-4" /> Add Exercise
        </Button>
      </div>

      <div className="space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              data-testid="exercise-search-input"
              className="pl-9"
              placeholder="Search by name, muscle, body part or equipment..."
              value={search}
              onChange={(e) => setSearchAndReset(e.target.value)}
            />
          </div>
          <Button
            type="button"
            variant={activeFilterCount > 0 ? "default" : "outline"}
            onClick={() => setShowFilters((s) => !s)}
            data-testid="exercise-toggle-filters"
            className="gap-2 shrink-0"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-white/20 text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </div>

        {showFilters && (
          <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3" data-testid="exercise-filter-panel">
            <div className="flex flex-wrap gap-3">
              <FilterSelect
                label="Category"
                value={category}
                onChange={setCategory}
                options={CATEGORIES}
                testid="exercise-filter-category"
              />
              <FilterSelect
                label="Equipment"
                value={equipment}
                onChange={setEquipment}
                options={equipmentOptions}
                testid="exercise-filter-equipment"
              />
              <FilterSelect
                label="Difficulty"
                value={difficulty}
                onChange={setDifficulty}
                options={DIFFICULTIES}
                testid="exercise-filter-difficulty"
              />
              <FilterSelect
                label="Target muscle"
                value={targetMuscle}
                onChange={setTargetMuscle}
                options={muscleOptions}
                testid="exercise-filter-muscle"
              />
            </div>
            {activeFilterCount > 0 && (
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  data-testid="exercise-clear-filters"
                  className="gap-1.5 text-xs"
                >
                  <X className="w-3 h-3" />
                  Clear all
                </Button>
              </div>
            )}
          </div>
        )}

        <p className="text-xs text-muted-foreground" data-testid="exercise-result-count">
          {filtered.length.toLocaleString()} match{filtered.length === 1 ? "" : "es"}
          {filtered.length > 0 && ` · page ${pageSafe} of ${totalPages}`}
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-16 text-center">
            <BookOpen className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="font-medium text-muted-foreground">
              {exercises.length === 0 ? "No exercises yet" : "No matches found"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {pageItems.map((ex) => (
              <Card
                key={ex.id}
                data-testid={`exercise-card-${ex.id}`}
                className="border-0 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {ex.media ? (
                      <img
                        src={ex.media}
                        alt={ex.name}
                        className="w-24 h-24 rounded-lg object-cover shrink-0 bg-muted"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-lg relative overflow-hidden bg-muted shrink-0">
                        <img
                          src={getBodyTargetImage(ex.category)}
                          alt={ex.category}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black/25 flex items-center justify-center">
                          <Dumbbell className="w-6 h-6 text-white/70" />
                        </div>
                      </div>
                    )}
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <p className="font-semibold text-sm capitalize leading-tight">{ex.name}</p>

                      <div className="flex gap-1 flex-wrap">
                        <Badge
                          className={`text-[10px] capitalize border ${CATEGORY_COLORS[ex.category] || CATEGORY_COLORS.full_body}`}
                        >
                          {ex.category === "full_body" ? "Full Body" : ex.category}
                        </Badge>
                        {ex.equipments.slice(0, 2).map((eq) => (
                          <Badge
                            key={eq}
                            variant="outline"
                            className="text-[10px] capitalize border-amber-500/20 text-amber-400 bg-amber-500/5"
                          >
                            {eq}
                          </Badge>
                        ))}
                        {ex.difficulty && (
                          <Badge
                            className={`text-[10px] capitalize border ${DIFFICULTY_COLORS[ex.difficulty] || ""}`}
                          >
                            {ex.difficulty}
                          </Badge>
                        )}
                      </div>

                      {ex.targetMuscles.length > 0 && (
                        <div className="flex gap-1 flex-wrap items-center">
                          <span className="text-[10px] text-muted-foreground">🎯</span>
                          {ex.targetMuscles.map((m) => (
                            <Badge
                              key={`t-${m}`}
                              className="text-[10px] capitalize border bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                            >
                              {m}
                            </Badge>
                          ))}
                        </div>
                      )}

                      {ex.secondaryMuscles.length > 0 && (
                        <div className="flex gap-1 flex-wrap items-center">
                          <span className="text-[10px] text-muted-foreground">+</span>
                          {ex.secondaryMuscles.map((m) => (
                            <Badge
                              key={`s-${m}`}
                              variant="outline"
                              className="text-[10px] capitalize bg-purple-500/5 text-purple-300/90 border-purple-500/20"
                            >
                              {m}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    {user?.role === "admin" && (
                      <div className="flex flex-col gap-1 shrink-0">
                        <Button
                          data-testid={`exercise-edit-${ex.id}`}
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => { setEditing(rawById[ex.id] || ex); setShowForm(true); }}
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button
                          data-testid={`exercise-delete-${ex.id}`}
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => deleteMutation.mutate(ex.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2" data-testid="exercise-pagination">
              <Button
                variant="outline"
                size="sm"
                disabled={pageSafe <= 1}
                onClick={() => setPage(pageSafe - 1)}
                data-testid="exercise-page-prev"
                className="gap-1"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Prev
              </Button>
              <span className="text-xs font-medium text-muted-foreground px-3">
                Page <span className="text-foreground font-bold">{pageSafe}</span> / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={pageSafe >= totalPages}
                onClick={() => setPage(pageSafe + 1)}
                data-testid="exercise-page-next"
                className="gap-1"
              >
                Next
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </>
      )}

      <ExerciseFormDialog
        open={showForm}
        onOpenChange={setShowForm}
        onSubmit={handleSubmit}
        initialData={editing}
      />
    </div>
  );
}
